import React from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

// TypeScript interfaces
interface Plan {
  id: number | string;
  name: string;
  price: string | number;
  savings_price?: string | number;
  features: string; // Pipe-separated features
  trial?: boolean;
  product_name: string;
  tagline?: string;
  savings?: string | number;
  label?: string; // For savings labels
}

interface PlansProps {
  plans: Plan[];
  initialIndex?: number;
}

/**
 * Plans Component - Matches original functionality from master branch
 * Displays subscription plans in a tabbed interface with savings labels
 */
const Plans: React.FC<PlansProps> = ({ plans, initialIndex = 1 }) => {
  const onSelect = (i: number) => { 
    // Original had empty function
  };

  return (
    <Tabs 
      className="sub_plans" 
      defaultIndex={initialIndex} 
      onSelect={(index, lastIndex, event) => onSelect(index)}
    >
      <SavingsLabels plans={plans} />
      
      <TabList>
        {plans.map((plan, index) => (
          <Tab key={plan.id || index}>{plan.name}</Tab>
        ))}
      </TabList>
      
      {plans.map((plan, index) => (
        <TabPanel key={plan.id || index}>
          <PlanComponent {...plan} />
        </TabPanel>
      ))}
    </Tabs>
  );
};

/**
 * SavingsLabels Component - Matches original implementation
 */
const SavingsLabels: React.FC<{ plans: Plan[] }> = ({ plans }) => (
  <div className="plan-savings-labels">
    {plans.map((plan, index) => (
      <div key={plan.id || index} className="plan-savings-label-container">
        {plan.label && (
          <span className="plan-savings-label">{plan.label}</span>
        )}
      </div>
    ))}
  </div>
);

/**
 * PlanComponent - Individual plan display (matches original Plan function)
 */
const PlanComponent: React.FC<Plan> = ({
  name,
  price,
  id,
  savings_price,
  features,
  trial = false,
  product_name,
  tagline,
  savings
}) => {
  const plan_features = features ? features.split('|') : [];
  const displayPrice = savings_price || price;
  const originalPrice = price;
  const hasSavings = savings_price && savings_price !== price;

  return (
    <div className="plan-container">
      <div className="plan color-1 dropshadow">
        <div className="plan-price">
          <h3 className="promo-yellow">{name}</h3>
          
          {hasSavings && savings && (
            <small className="billing-savings">{savings}% Savings!</small>
          )}
          
          <span className="plan-currency">$ </span>
          <span className="value">{displayPrice}</span> / mo
          
          {hasSavings && (
            <small className="billing-term">
              (${originalPrice} Billed {name.includes('ly') ? name : `${name}ly`})
            </small>
          )}
          
          <p className="promo-yellow">
            <small>{product_name}</small>
          </p>
        </div>

        <div className="plan-features landing">
          <ul>
            {tagline && (
              <li className="centered">
                <strong>{tagline}</strong>
              </li>
            )}
            {plan_features.map((feature, index) => (
              <li key={index}>
                <i className="fa fa-check"></i> {feature.trim()}
              </li>
            ))}
          </ul>
          
          <a className="stripe-button-el" href={`/plans/${id}`}>
            <span>Get Started</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Plans;
